// src/modules/app-views/account/pages/Subscription/modals/DepositSparksModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useMemo, useState } from 'react';
import { PiHandCoins } from 'react-icons/pi';
import dayjs from 'dayjs';
import GenericModal from 'components/core/GenericModal';
import accountService from 'modules/app-views/account/service';
import authService from 'services/authService';
import useZustand from 'hooks/useZustand';
import { generateMongoObjectId } from 'utils/helpers';
import depositSparksStep from './Step1';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DepositSparksModal = ({ show, setShow, afterClose, record }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedUser = useZustand('auth', 'user');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [operation, setOperation] = useState('credit');
  const [movements, setMovements] = useState(record?.sparkMovements || []);
  const [runningBalance, setRunningBalance] = useState(record?.sparkCoins || 0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(
    ({ amount, description }) => {
      const delta = operation === 'credit' ? amount : -amount;
      const newBalance = runningBalance + delta;

      const newMovement = {
        _id: generateMongoObjectId(),
        createdAt: dayjs().toISOString(),
        description,
        userName: storedUser?.name || '',
        delta,
        balance: newBalance,
      };

      // Optimistic update: append at end to maintain ascending chronological order
      setMovements(prev => [...prev, newMovement]);
      setRunningBalance(newBalance);

      // Fire PUT silently in background (with description + userName for persistence)
      accountService.depositSparkCoins({
        accountId: record?.accountId,
        amount: delta,
        description,
        userName: storedUser?.name || '',
      });
    },
    [operation, runningBalance, storedUser, record],
  );

  const handleAfterClose = useCallback(() => {
    // Refresh auth store to sync sparkCoins balance in app state
    authService.signInRefresh();
    afterClose?.();
  }, [afterClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      depositSparksStep({
        record,
        currentBalance: runningBalance,
        operation,
        onOperationChange: setOperation,
        movements,
        onConfirm: handleConfirm,
      }),
    ],
    [record, runningBalance, operation, movements, handleConfirm],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<PiHandCoins />}
      title="deposit_spark_coins"
      steps={steps}
      afterClose={handleAfterClose}
      width={720}
      okText="close"
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DepositSparksModal;
